/**
 * Chart Export Utility
 * Exports charts and canvas elements as high-resolution PNG images
 * Uses html2canvas for rendering HTML elements to canvas
 */

import html2canvas from 'html2canvas';
import { generateFilename } from './csv-export';

export interface ChartExportOptions {
  /**
   * Scale factor for high-resolution export (default: 2 for retina)
   */
  scale?: number;
  /**
   * Background color (default: white)
   */
  backgroundColor?: string;
  /**
   * Whether to add padding around the chart
   */
  padding?: number;
  /**
   * Image quality for JPEG exports (0-1)
   */
  quality?: number;
  /**
   * Output format
   */
  format?: 'png' | 'jpeg' | 'webp';
  /**
   * Carrier name for filename
   */
  carrierName?: string;
  /**
   * Whether to ignore elements with data-html2canvas-ignore attribute
   */
  ignoreElements?: (element: Element) => boolean;
}

const DEFAULT_OPTIONS: ChartExportOptions = {
  scale: 2,
  backgroundColor: '#fbf9f4',
  padding: 20,
  quality: 0.95,
  format: 'png',
};

/**
 * Export a chart element as PNG image
 */
export const exportChartAsPNG = async (
  elementId: string,
  filename: string,
  options: ChartExportOptions = {}
): Promise<boolean> => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return false;
  }

  return exportElementAsImage(element, filename, options);
};

/**
 * Export any HTML element as an image
 */
export const exportElementAsImage = async (
  element: HTMLElement,
  filename: string,
  options: ChartExportOptions = {}
): Promise<boolean> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // Configure html2canvas options
    const canvasOptions: Record<string, unknown> = {
      scale: opts.scale,
      backgroundColor: opts.backgroundColor,
      useCORS: true,
      allowTaint: true,
      logging: false,
      // Handle SVG and cross-origin images
      foreignObjectRendering: true,
      removeContainer: true,
    };

    if (opts.ignoreElements) {
      canvasOptions.ignoreElements = opts.ignoreElements;
    }

    // Render element to canvas
    const canvas = await html2canvas(element, canvasOptions);

    // Add padding if specified
    let finalCanvas = canvas;
    if (opts.padding && opts.padding > 0) {
      finalCanvas = addPaddingToCanvas(canvas, opts.padding, opts.backgroundColor || '#fbf9f4');
    }

    // Convert to data URL
    const mimeType = `image/${opts.format}`;
    const dataUrl = finalCanvas.toDataURL(mimeType, opts.quality);

    // Generate filename
    const ext = opts.format || 'png';
    const finalFilename = generateFilename(filename, opts.carrierName, ext);

    // Trigger download
    downloadDataUrl(dataUrl, finalFilename);

    return true;
  } catch (error) {
    console.error('Failed to export chart:', error);
    return false;
  }
};

/**
 * Export multiple charts as separate images
 */
export const exportMultipleCharts = async (
  charts: Array<{ elementId: string; filename: string }>,
  options: ChartExportOptions = {},
  onProgress?: (current: number, total: number) => void
): Promise<{ success: number; failed: number }> => {
  let success = 0;
  let failed = 0;

  for (let i = 0; i < charts.length; i++) {
    const chart = charts[i];
    const result = await exportChartAsPNG(chart.elementId, chart.filename, options);

    if (result) {
      success++;
    } else {
      failed++;
    }

    if (onProgress) {
      onProgress(i + 1, charts.length);
    }

    // Small delay between exports to prevent browser issues
    if (i < charts.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  return { success, failed };
};

/**
 * Export Recharts chart (specifically handles Recharts responsive containers)
 */
export const exportRechartsChart = async (
  containerId: string,
  filename: string,
  options: ChartExportOptions = {}
): Promise<boolean> => {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with id "${containerId}" not found`);
    return false;
  }

  // Find the SVG element inside the Recharts container
  const svg = container.querySelector('svg');
  if (!svg) {
    console.error('No SVG element found in Recharts container');
    return false;
  }

  // Get the parent that includes the chart wrapper
  const chartWrapper = svg.closest('.recharts-wrapper');
  const exportElement = (chartWrapper as HTMLElement) || container;

  return exportElementAsImage(exportElement, filename, options);
};

/**
 * Capture a screenshot of the entire dashboard
 */
export const captureScreenshot = async (
  elementSelector: string,
  filename: string,
  options: ChartExportOptions = {}
): Promise<boolean> => {
  const element = document.querySelector(elementSelector) as HTMLElement;
  if (!element) {
    console.error(`Element matching "${elementSelector}" not found`);
    return false;
  }

  // For full page captures, we might need to temporarily modify styles
  const originalOverflow = element.style.overflow;
  element.style.overflow = 'visible';

  try {
    const result = await exportElementAsImage(element, filename, {
      ...options,
      // Full page captures often need lower scale to avoid memory issues
      scale: options.scale || 1.5,
    });
    return result;
  } finally {
    element.style.overflow = originalOverflow;
  }
};

/**
 * Export chart data as JSON (useful for debugging or data backup)
 */
export const exportChartDataAsJSON = (
  data: unknown,
  filename: string,
  carrierName?: string
): void => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const finalFilename = generateFilename(filename, carrierName, 'json');
  downloadDataUrl(url, finalFilename);
  URL.revokeObjectURL(url);
};

/**
 * Create a composite image from multiple chart elements
 */
export const createCompositeChartImage = async (
  elementIds: string[],
  filename: string,
  options: ChartExportOptions & {
    layout?: 'horizontal' | 'vertical' | 'grid';
    columns?: number;
    gap?: number;
  } = {}
): Promise<boolean> => {
  const { layout = 'vertical', columns = 2, gap = 20 } = options;
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // Capture all elements
    const canvases: HTMLCanvasElement[] = [];
    for (const id of elementIds) {
      const element = document.getElementById(id);
      if (element) {
        const canvas = await html2canvas(element, {
          scale: opts.scale,
          backgroundColor: 'transparent',
          useCORS: true,
          logging: false,
        });
        canvases.push(canvas);
      }
    }

    if (canvases.length === 0) {
      console.error('No elements found to compose');
      return false;
    }

    // Calculate composite dimensions
    let compositeWidth = 0;
    let compositeHeight = 0;

    if (layout === 'horizontal') {
      compositeWidth = canvases.reduce((sum, c) => sum + c.width, 0) + (canvases.length - 1) * gap;
      compositeHeight = Math.max(...canvases.map(c => c.height));
    } else if (layout === 'vertical') {
      compositeWidth = Math.max(...canvases.map(c => c.width));
      compositeHeight = canvases.reduce((sum, c) => sum + c.height, 0) + (canvases.length - 1) * gap;
    } else {
      // Grid layout
      const rows = Math.ceil(canvases.length / columns);
      const maxColWidth = canvases.reduce((max, c, i) => {
        const col = i % columns;
        return Math.max(max, c.width);
      }, 0);
      const maxRowHeight = canvases.reduce((max, c, i) => {
        return Math.max(max, c.height);
      }, 0);
      compositeWidth = columns * maxColWidth + (columns - 1) * gap;
      compositeHeight = rows * maxRowHeight + (rows - 1) * gap;
    }

    // Add padding
    const padding = opts.padding || 0;
    compositeWidth += padding * 2;
    compositeHeight += padding * 2;

    // Create composite canvas
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = compositeWidth;
    compositeCanvas.height = compositeHeight;

    const ctx = compositeCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Fill background
    ctx.fillStyle = opts.backgroundColor || '#fbf9f4';
    ctx.fillRect(0, 0, compositeWidth, compositeHeight);

    // Draw canvases
    let x = padding;
    let y = padding;

    canvases.forEach((canvas, index) => {
      if (layout === 'horizontal') {
        ctx.drawImage(canvas, x, padding);
        x += canvas.width + gap;
      } else if (layout === 'vertical') {
        ctx.drawImage(canvas, padding, y);
        y += canvas.height + gap;
      } else {
        // Grid
        const col = index % columns;
        const row = Math.floor(index / columns);
        const gridX = padding + col * (canvas.width + gap);
        const gridY = padding + row * (canvas.height + gap);
        ctx.drawImage(canvas, gridX, gridY);
      }
    });

    // Convert to data URL and download
    const mimeType = `image/${opts.format || 'png'}`;
    const dataUrl = compositeCanvas.toDataURL(mimeType, opts.quality);
    const finalFilename = generateFilename(filename, opts.carrierName, opts.format || 'png');
    downloadDataUrl(dataUrl, finalFilename);

    return true;
  } catch (error) {
    console.error('Failed to create composite image:', error);
    return false;
  }
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Add padding to a canvas
 */
const addPaddingToCanvas = (
  canvas: HTMLCanvasElement,
  padding: number,
  backgroundColor: string
): HTMLCanvasElement => {
  const paddedCanvas = document.createElement('canvas');
  paddedCanvas.width = canvas.width + padding * 2;
  paddedCanvas.height = canvas.height + padding * 2;

  const ctx = paddedCanvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);

  // Draw original canvas centered
  ctx.drawImage(canvas, padding, padding);

  return paddedCanvas;
};

/**
 * Download a data URL as a file
 */
const downloadDataUrl = (dataUrl: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get image dimensions from a data URL
 */
export const getImageDimensions = (
  dataUrl: string
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
};

/**
 * Convert canvas to blob for upload or further processing
 */
export const canvasToBlob = (
  canvas: HTMLCanvasElement,
  format: string = 'image/png',
  quality: number = 0.95
): Promise<Blob | null> => {
  return new Promise(resolve => {
    canvas.toBlob(resolve, format, quality);
  });
};
